/**
 * File: OCRMetricsDashboard.jsx
 * Path: /src/components/solutions/OCRMetricsDashboard.jsx
 * Description: Standalone dashboard visualization for the OCR AI solution
 * Key Features: Interactive OCR performance metrics with analytics visualization
 */

import React from 'react';
import { motion } from 'framer-motion';

const OCRMetricsDashboard = () => {
  return (
    <div className="relative rounded-xl overflow-hidden shadow-xl w-full">
      <div className="bg-gradient-to-br from-indigo-700 to-purple-700 p-6">
        {/* Circuit pattern */}
        <div className="absolute inset-0 opacity-20">
          <svg width="100%" height="100%" viewBox="0 0 800 600" xmlns="http://www.w3.org/2000/svg">
            <g fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M769.5,591.5 L417.5,591.5 L417.5,196.5 L365.5,196.5 L365.5,591.5 L57.5,591.5" stroke="rgba(255,255,255,0.3)" />
              <path d="M627.5,358.5 L627.5,520.5 L493.5,520.5 L493.5,421.5 L549.5,421.5" stroke="rgba(255,255,255,0.4)" />
              <path d="M595.5,308.5 L696.5,308.5 L696.5,224.5 L492.5,224.5 L492.5,87.5 L696.5,87.5" stroke="rgba(255,255,255,0.25)" />
              <path d="M44.5,358.5 L44.5,144.5 L200.5,144.5 L200.5,242.5 L154.5,242.5" stroke="rgba(255,255,255,0.3)" />
            </g>
            <g fill="rgba(255,255,255,0.5)">
              <circle cx="492.5" cy="224.5" r="4" />
              <circle cx="365.5" cy="196.5" r="4" />
              <circle cx="627.5" cy="358.5" r="4" />
              <circle cx="200.5" cy="144.5" r="4" />
              <circle cx="549.5" cy="421.5" r="4" />
            </g>
          </svg>
        </div>
        
        {/* Dashboard container with min-height to prevent cutting off */}
        <div className="min-h-[440px] w-full flex items-center justify-center relative z-10">
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-6 w-full text-white">
            {/* Interface Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="font-bold text-xl">Veritect OCR Analytics Dashboard</h3>
              </div>
              <div className="flex space-x-2">
                <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                <div className="w-3 h-3 bg-green-400 rounded-full"></div>
              </div>
            </div>
            
            {/* Main Dashboard Content */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              {/* Overall OCR Accuracy */}
              <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4 text-center">
                <div className="text-sm font-medium mb-2">Overall Accuracy</div>
                <div className="text-3xl font-bold mb-2">99.8%</div>
                <div className="text-xs text-indigo-200">+0.3% from last month</div>
              </div>
              
              {/* Documents Processed */}
              <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4 text-center">
                <div className="text-sm font-medium mb-2">Documents Processed</div>
                <div className="text-3xl font-bold mb-2">127,458</div>
                <div className="text-xs text-green-300">+14% from last month</div>
              </div>
              
              {/* Average Processing Time */}
              <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4 text-center">
                <div className="text-sm font-medium mb-2">Avg. Processing Time</div>
                <div className="text-3xl font-bold mb-2">1.4s</div>
                <div className="text-xs text-green-300">-0.3s from last month</div>
              </div>
            </div>
            
            {/* Document Type Performance */}
            <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4 mb-6">
              <div className="flex justify-between items-center mb-4">
                <div className="text-sm font-medium">Accuracy by Document Type</div>
                <div className="text-xs text-white/60">Last 30 days</div>
              </div>
              
              {/* Bar chart visualization */}
              <div className="grid grid-cols-5 gap-4 text-sm">
                <div className="col-span-1 text-right">
                  <div className="mb-2 opacity-80">Court Filings</div>
                  <div className="mb-2 opacity-80">Contracts</div>
                  <div className="mb-2 opacity-80">Exhibits</div>
                  <div className="mb-2 opacity-80">Discovery</div>
                  <div className="mb-2 opacity-80">Handwritten</div>
                </div>
                
                <div className="col-span-4">
                  {/* Court Filings Bar */}
                  <div className="mb-2">
                    <div className="h-6 flex items-center">
                      <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-r-sm rounded-l-sm" style={{ width: '99.7%' }}>
                        <div className="px-2 text-xs font-medium text-right pr-3">99.7%</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Contracts Bar */}
                  <div className="mb-2">
                    <div className="h-6 flex items-center">
                      <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-r-sm rounded-l-sm" style={{ width: '99.9%' }}>
                        <div className="px-2 text-xs font-medium text-right pr-3">99.9%</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Exhibits Bar */}
                  <div className="mb-2">
                    <div className="h-6 flex items-center">
                      <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-r-sm rounded-l-sm" style={{ width: '99.5%' }}>
                        <div className="px-2 text-xs font-medium text-right pr-3">99.5%</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Discovery Bar */}
                  <div className="mb-2">
                    <div className="h-6 flex items-center">
                      <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-r-sm rounded-l-sm" style={{ width: '99.4%' }}>
                        <div className="px-2 text-xs font-medium text-right pr-3">99.4%</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Handwritten Bar */}
                  <div className="mb-2">
                    <div className="h-6 flex items-center">
                      <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-r-sm rounded-l-sm" style={{ width: '95.2%' }}>
                        <div className="px-2 text-xs font-medium text-right pr-3">95.2%</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Processing Volume Trend and Top Errors */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Processing Volume Trend */}
              <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4">
                <div className="text-sm font-medium mb-4">Processing Volume (Documents/Day)</div>
                
                {/* Mock Line Chart */}
                <div className="relative h-40">
                  {/* Chart axis */}
                  <div className="absolute left-0 top-0 bottom-0 w-px bg-white/20"></div>
                  <div className="absolute left-0 right-0 bottom-0 h-px bg-white/20"></div>
                  
                  {/* Chart line */}
                  <svg width="100%" height="100%" viewBox="0 0 300 150" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="line-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#818cf8" />
                        <stop offset="100%" stopColor="#c084fc" />
                      </linearGradient>
                    </defs>
                    <path 
                      d="M0,100 C20,90 40,120 60,100 C80,80 100,130 120,110 C140,90 160,100 180,80 C200,60 220,70 240,50 C260,30 280,60 300,40" 
                      fill="none" 
                      stroke="url(#line-gradient)" 
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                  </svg>
                  
                  {/* Data points */}
                  <div className="absolute left-[10%] top-[60%] w-3 h-3 bg-purple-500 rounded-full"></div>
                  <div className="absolute left-[30%] top-[66%] w-3 h-3 bg-purple-500 rounded-full"></div>
                  <div className="absolute left-[50%] top-[53%] w-3 h-3 bg-purple-500 rounded-full"></div>
                  <div className="absolute left-[70%] top-[33%] w-3 h-3 bg-purple-500 rounded-full"></div>
                  <div className="absolute left-[90%] top-[26%] w-3 h-3 bg-purple-500 rounded-full"></div>
                </div>
                
                {/* Day labels */}
                <div className="flex justify-between text-xs text-white/60 mt-2">
                  <div>Mon</div>
                  <div>Tue</div>
                  <div>Wed</div>
                  <div>Thu</div>
                  <div>Fri</div>
                  <div>Sat</div>
                  <div>Sun</div>
                </div>
              </div>
              
              {/* Top Errors */}
              <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4">
                <div className="text-sm font-medium mb-4">Top Recognition Challenges</div>
                
                <div className="space-y-3">
                  {/* Error item 1 */}
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center mr-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center">
                        <div className="text-sm">Handwritten signatures</div>
                        <div className="text-xs text-white/60">4.2% error rate</div>
                      </div>
                      <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full bg-yellow-500 rounded-full" style={{ width: '4.2%' }}></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Error item 2 */}
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center mr-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center">
                        <div className="text-sm">Faded text in old documents</div>
                        <div className="text-xs text-white/60">2.8% error rate</div>
                      </div>
                      <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full bg-yellow-500 rounded-full" style={{ width: '2.8%' }}></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Error item 3 */}
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center mr-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center">
                        <div className="text-sm">Complex legal symbols</div>
                        <div className="text-xs text-white/60">1.2% error rate</div>
                      </div>
                      <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500 rounded-full" style={{ width: '1.2%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OCRMetricsDashboard;